import React from 'react';
import { Icon } from '@repo/ui';
import { Button, Card, CardHeader, CardBody, Badge } from '@repo/ui';
import type { Template } from '../types';
import { parseTemplateTags, getApiUrl } from '../utils';

// Standardized button styles
const BUTTON_STYLES = {
  standard: {
    variant: 'outline' as const,
    size: 'sm' as const,
  },
  destructive: {
    variant: 'destructive' as const,
    size: 'sm' as const,
  },
} as const;

const BUTTON_CLASSES = {
  group: 'gap-2',
} as const;

interface TemplateCardProps {
  template: Template;
  isMaster?: boolean;
  onTest: (templateId: string) => void;
  onDelete: (templateId: string) => void;
  isDeleting: boolean;
}

const getTemplateIcon = (type: string) => {
  switch (type) {
    case 'pdf':
      return <Icon name="HiDocumentAdd" className="h-5 w-5 text-red-600" />;
    case 'email':
      return <Icon name="HiMail" className="h-5 w-5 text-blue-600" />;
    default:
      return <Icon name="HiDocumentText" className="h-5 w-5 text-xtnd-dark-500" />;
  }
};

export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  isMaster = false,
  onTest,
  onDelete,
  isDeleting,
}) => {
  const tags = parseTemplateTags(template.tags);

  return (
    <Card className="hover:shadow-lg transition-shadow flex flex-col ">
      <CardHeader className="pb-3">
        <div className="flex w-full items-start justify-between">
          {/* LEFT: icon + text (flexible) */}
          <div className="flex flex-1 basis-0 min-w-0 items-center gap-3">
            <div className="shrink-0">{getTemplateIcon(template.templateType)}</div>

            <div className="min-w-0">
              <h3
                className="font-semibold text-sm text-xtnd-dark-900 dark:text-xtnd-white truncate"
                title={template.templateName}
              >
                {template.templateName}
              </h3>
              <p className="text-sm text-xtnd-dark-900 dark:text-xtnd-dark-300 truncate">
                v{template.version}
              </p>
            </div>
          </div>

          {/* RIGHT: badges (don't shrink) */}
          <div className="flex shrink-0 flex-col items-end gap-2">
            <Badge
              className={`flex items-center justify-center ${
                template.isActive
                  ? 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-100'
                  : 'bg-xtnd-dark-50 text-xtnd-dark-700'
              }`}
            >
              {template.isActive ? 'Active' : 'Inactive'}
            </Badge>

            <Badge className="flex items-center justify-center bg-xtnd-light text-xtnd-dark dark:bg-xtnd-light-900 dark:text-xtnd-dark-100">
              {template.templateType.toUpperCase()}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardBody className="pt-0 flex flex-1 flex-col">
        <p className="text-sm text-xtnd-dark-900 dark:text-xtnd-dark-300 mb-4">
          {template.description || 'No description available'}
        </p>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {tags.map((tag, index) => (
              <span key={index} className="px-2 py-1 bg-xtnd-dark-50 text-xtnd-dark-600 text-xs rounded">
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-col items-begin justify-between text-xs text-xtnd-dark-600 dark:text-xtnd-dark-300 mb-4 ">
          <span className="text-bold">
            Size: <span className="">{(template.fileSize / 1024).toFixed(1)}</span>KB
          </span>
          <span>Created: {new Date(template.createdAt).toLocaleDateString()}</span>
          <span>By: {template.createdBy}</span>
        </div>

        {!isMaster && !template.hasTemplateContent && (
          <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
            ⚠️ Re-upload needed: This template was uploaded before content storage. Re-upload to
            enable .hbs downloads.
          </div>
        )}

        {template.variables && Object.keys(template.variables).length > 0 && (
          <div className="mb-4">
            <p className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light mb-2">
              Template Variables:
            </p>
            <div className="flex flex-wrap gap-1">
              {Object.keys(template.variables).map((variable, index) => (
                <code
                  key={index}
                  className="px-2 py-1 bg-xtnd-dark-50 text-xtnd-dark-700 text-xs rounded border dark:bg-xtnd-dark-700 dark:text-xtnd-light dark:border-xtnd-dark-500"
                >
                  {'{{'}
                  {variable}
                  {'}}'}
                </code>
              ))}
            </div>
          </div>
        )}

        <div className={`flex flex-wrap items-center space-x-2 mt-auto ${BUTTON_CLASSES.group}`}>
          <Button
            {...BUTTON_STYLES.standard}
            onClick={() => onTest(template.templateId)}
            leftIcon={<Icon name="HiPlay" className="h-4 w-4" />}
          >
            Test
          </Button>

          {!isMaster && template.hasTemplateContent && (
            <Button
              {...BUTTON_STYLES.standard}
              onClick={() => {
                const url = getApiUrl(`/templates/${template.templateId}/download`);
                window.open(url, '_blank');
              }}
              title="Download .hbs template file"
              leftIcon={<Icon name="HiDownload" className="h-4 w-4" />}
            >
              .hbs
            </Button>
          )}

          {!isMaster && !template.hasTemplateContent && (
            <Button
              {...BUTTON_STYLES.standard}
              disabled
              title="Template content not stored. Re-upload to enable download."
              leftIcon={<Icon name="HiDownload" className="h-4 w-4" />}
            >
              .hbs
            </Button>
          )}

          {isMaster && (
            <Button
              {...BUTTON_STYLES.standard}
              leftIcon={<Icon name="HiDownload" className="h-4 w-4" />}
            >
              Copy
            </Button>
          )}

          {!isMaster && (
            <Button
              {...BUTTON_STYLES.destructive}
              onClick={() => onDelete(template.templateId)}
              disabled={isDeleting}
              leftIcon={<Icon name="HiTrash" className="h-4 w-4" />}
            >
              Delete
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

